import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@material-ui/core';
import { MEMBERSHIP_ROLE_OPTIONS } from './memberLabels';

const emptyForm = {
  email: '',
  name: '',
  mobile: '',
  role: 'TEACHER',
  studentMode: 'NEW',
  studentId: '',
  studentName: '',
  studentDateOfBirth: '',
};

/** Builds the `POST members` body; returns `{ error }` when the form is incomplete. */
const toAddPayload = (form) => {
  if (!form.email.trim()) return { error: 'Email is required' };
  const payload = { email: form.email.trim(), role: form.role };
  if (form.name.trim()) payload.name = form.name.trim();
  if (form.mobile.trim()) payload.mobile = form.mobile.trim();

  // The backend 400s a PARENT without a child, and anyone else with one.
  if (form.role === 'PARENT') {
    if (form.studentMode === 'EXISTING') {
      if (!form.studentId.trim()) return { error: 'Student ID is required' };
      payload.student = { studentId: form.studentId.trim() };
    } else {
      if (!form.studentName.trim() || !form.studentDateOfBirth)
        return { error: "The child's name and date of birth are required" };
      payload.student = {
        name: form.studentName.trim(),
        dateOfBirth: form.studentDateOfBirth,
      };
    }
  }
  return { payload };
};

/**
 * Add (`member` null) or edit a school member. Edit sends `name` only: email and mobile
 * are identity keys and roles come with licences, so the backend refuses all three.
 */
const MemberFormDialog = ({ open, member, onClose, onSubmit }) => {
  const isEdit = !!member;
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setError('');
    setForm(
      member
        ? {
            ...emptyForm,
            email: member.user.email || '',
            name: member.user.name || '',
            mobile: member.user.mobile || '',
          }
        : emptyForm
    );
  }, [open, member]);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = () => {
    if (isEdit) {
      if (!form.name.trim()) return setError('Name is required');
      return onSubmit({ name: form.name.trim() });
    }
    const { payload, error: formError } = toAddPayload(form);
    if (formError) return setError(formError);
    onSubmit(payload);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {isEdit ? 'Edit School Member' : 'Add School Member'}
      </DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
          <TextField
            size="small"
            label="Email"
            type="email"
            variant="outlined"
            required
            value={form.email}
            onChange={set('email')}
            disabled={isEdit}
            helperText={isEdit ? 'Email cannot be changed' : ''}
          />
          <TextField
            size="small"
            label="Name"
            variant="outlined"
            required={isEdit}
            value={form.name}
            onChange={set('name')}
            helperText={isEdit ? '' : 'Defaults from the email if left empty'}
          />
          <TextField
            size="small"
            label="Mobile"
            variant="outlined"
            value={form.mobile}
            onChange={set('mobile')}
            disabled={isEdit}
            helperText={isEdit ? 'Mobile cannot be changed' : ''}
          />

          {!isEdit && (
            <TextField
              size="small"
              select
              label="Membership Role"
              variant="outlined"
              value={form.role}
              onChange={set('role')}>
              {MEMBERSHIP_ROLE_OPTIONS.map(({ value, label }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          )}

          {!isEdit && form.role === 'PARENT' && (
            <Box>
              <Typography variant="subtitle2">Child</Typography>
              <RadioGroup
                row
                value={form.studentMode}
                onChange={set('studentMode')}>
                <FormControlLabel
                  value="NEW"
                  control={<Radio size="small" />}
                  label="New child"
                />
                <FormControlLabel
                  value="EXISTING"
                  control={<Radio size="small" />}
                  label="Existing student"
                />
              </RadioGroup>
              {form.studentMode === 'EXISTING' ? (
                <TextField
                  size="small"
                  fullWidth
                  label="Student ID"
                  variant="outlined"
                  value={form.studentId}
                  onChange={set('studentId')}
                />
              ) : (
                <Box display="flex" style={{ gap: 16 }}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Child's name"
                    variant="outlined"
                    value={form.studentName}
                    onChange={set('studentName')}
                  />
                  <TextField
                    size="small"
                    fullWidth
                    label="Date of birth"
                    type="date"
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    value={form.studentDateOfBirth}
                    onChange={set('studentDateOfBirth')}
                  />
                </Box>
              )}
            </Box>
          )}

          {error && (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          {isEdit ? 'Save' : 'Add Member'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MemberFormDialog;
